import { Button, Chip, MenuItem, Stack, TextField } from "@mui/material";
import type { GridColDef } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";

import { DataTable, DateText, MoneyText, PageHeader, PermissionGate, StatusChip, useListParams } from "../components";
import { formatMoney } from "../i18n/format";
import help from "./HELP.md?raw";
import { useQuoteSummary, useQuotes, type Quote } from "./useData";

export function Quotes() {
  const navigate = useNavigate();
  const [params, setParams] = useListParams({ ordering: "-quote_date" });
  const list = useQuotes(params);
  const summary = useQuoteSummary();
  const columns: GridColDef<Quote>[] = [
    { field: "display_number", headerName: "Quote", width: 120 },
    { field: "customer_name", headerName: "Customer", width: 180 },
    { field: "title", headerName: "Title", flex: 1, minWidth: 180 },
    { field: "status", headerName: "Status", width: 140, renderCell: (p) => <StatusChip status={p.value as string} /> },
    { field: "total", headerName: "Total", width: 120, renderCell: (p) => <MoneyText amount={p.value as string} currency={p.row.currency} /> },
    { field: "margin_pct", headerName: "Margin %", width: 90 },
    { field: "quote_date", headerName: "Date", width: 110, renderCell: (p) => <DateText value={p.value as string} /> },
    { field: "valid_until", headerName: "Valid until", width: 110, renderCell: (p) => p.value ? <DateText value={p.value as string} /> : "" },
    { field: "salesperson_email", headerName: "Salesperson", width: 190 },
    { field: "rfq_number", headerName: "RFQ", width: 100 },
  ];
  const s = summary.data;
  return (
    <>
      <PageHeader help={help} title="Quotes" subtitle="Quotations, revisions, approvals and outcomes" crumbs={[{ label: "Quoting" }, { label: "Quotes" }]}
        actions={<PermissionGate permission="quoting.quote.create"><Button variant="contained" onClick={() => navigate("/app/quoting/quotes/new")}>New quote</Button></PermissionGate>} />
      {s && (
        <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: "wrap" }}>
          <Chip label={`Open value ${formatMoney(s.open_value, "USD")}`} variant="outlined" />
          <Chip label={`Pending approval ${s.pending_approval}`} color={s.pending_approval ? "warning" : "default"} onClick={() => setParams({ ...params, status: "pending_approval", page: 1 })} />
          <Chip label={`Expiring ≤7d ${s.expiring_soon}`} variant="outlined" />
          <Chip label={`Win rate 90d ${s.win_rate_90d ?? "—"}${s.win_rate_90d !== null ? "%" : ""}`} variant="outlined" />
        </Stack>
      )}
      <DataTable viewKey="quoting.quote.list" columns={columns} data={list.data} isLoading={list.isLoading} params={params} onParamsChange={setParams} exportPath="/api/v1/quoting/quotes/export/"
        onRowClick={(row) => navigate(`/app/quoting/quotes/${row.id}`)}
        filters={<>
          <TextField select size="small" label="Status" value={String(params.status ?? "")} onChange={(e) => setParams({ ...params, status: e.target.value, page: 1 })} sx={{ width: 160 }}><MenuItem value="">All</MenuItem>{["draft", "pending_approval", "approved", "sent", "accepted", "declined", "expired", "canceled"].map((k) => <MenuItem key={k} value={k}>{k.replace("_", " ")}</MenuItem>)}</TextField>
          <TextField select size="small" label="Revisions" value={String(params.latest ?? "")} onChange={(e) => setParams({ ...params, latest: e.target.value, page: 1 })} sx={{ width: 150 }}><MenuItem value="">Latest only</MenuItem><MenuItem value="false">Include superseded</MenuItem></TextField>
        </>} />
    </>
  );
}
